/**
 * A game in progress, or a game being read back afterwards.
 *
 * The position is never received — it is replayed from the move log through the same
 * engine the server runs, memoised on the log. Browsing history is therefore the same
 * operation as showing the current position with a smaller number, which is why it cannot
 * drift: the 2023 client had a separate path for prior states that forced `status` back
 * to `playing` and left the live clocks ticking underneath, so the board showed a
 * finished game as though it were still going.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { formatMove, type Move, type PlayerIndex } from '@gok/engine';
import { computeClocks, matchChannel, type MatchState, reduceMatch } from '@gok/shared';

import { useChannel, useConnection } from '../net/context.tsx';
import { UserName } from '../net/useUser.tsx';
import { useMatchGame } from '../game/useMatchGame.ts';
import { LIVE, useHistoryKeys } from '../game/history.ts';
import { type Selection, targetsFor } from '../game/selection.ts';
import { Board } from '../ui/Board.tsx';
import { Clock } from '../ui/Clock.tsx';
import { MoveList } from '../ui/MoveList.tsx';
import { Reserve } from '../ui/Reserve.tsx';
import { Button, Empty, Panel } from '../ui/primitives.tsx';
import { ConnectionBadge } from '../ui/Chrome.tsx';

const OUTCOME: Record<string, string> = {
  checkmate: 'Checkmate',
  resignation: 'Resignation',
  timeout: 'Flag fall',
  drawn: 'Draw agreed',
  repetition: 'Draw by repetition',
  aborted: 'Aborted',
};

export function MatchView({ id }: { id: string }) {
  const { userId, send, clockOffset, status } = useConnection();
  const { state: match, stale } = useChannel<MatchState, never>(
    matchChannel(id),
    reduceMatch,
  );

  const [ply, setPly] = useState<number>(LIVE);
  const [selection, setSelection] = useState<Selection>(null);
  /**
   * Which secondary panel is open on a phone. Both are always open on a wide screen —
   * this only decides how much room the board gives up, and on a phone the answer is
   * none by default. A squeezed desktop layout is not a mobile layout.
   */
  const [drawer, setDrawer] = useState<'none' | 'moves' | 'chat'>('none');

  const game = useMatchGame(match, ply);

  // A move landing invalidates whatever was held.
  const moveCount = match?.moves.length ?? 0;
  useEffect(() => setSelection(null), [moveCount, ply]);

  useHistoryKeys({ ply, moveCount, seek: setPly });

  const targets = useMemo(
    () => (game ? targetsFor(game.game.position, game.legalMoves, selection) : null),
    [game, selection],
  );

  const play = useCallback(
    (move: Move) => {
      if (!game) return;
      send({
        t: 'move',
        matchId: id,
        san: formatMove(game.game.position, move),
      });
    },
    [game, id, send],
  );

  if (!match || !game || !targets) {
    return (
      <div className='grid h-full place-items-center'>
        <p className='text-sm text-muted'>
          {status === 'ready' ? 'No such game.' : 'Connecting…'}
        </p>
      </div>
    );
  }

  const seat = match.players.indexOf(userId ?? '');
  const mySeat = (seat === 0 || seat === 1 ? seat : null) as PlayerIndex | null;
  const viewFrom: PlayerIndex = mySeat === 1 ? 1 : 0;

  const live = ply >= match.moves.length;
  const playing = match.status === 'playing';
  const myTurn = live && playing && mySeat !== null && mySeat === game.toMove;

  const topSeat = (viewFrom === 1 ? 0 : 1) as PlayerIndex;
  const bottomSeat = (viewFrom === 1 ? 1 : 0) as PlayerIndex;

  // Browsing history shows the clocks as they stood at that move, not as they stand now.
  // Running the same function over a prefix of the log is the only honest way to do it,
  // and is why it lives in `shared/` rather than on the server.
  const shown: MatchState = live ? match : {
    ...match,
    moves: match.moves.slice(0, ply),
    ...computeClocks(match.variant, match.moves.slice(0, ply), match.startedAt),
  };

  return (
    <div className='flex h-full min-h-0 flex-col lg:flex-row'>
      <div className='relative flex min-h-0 flex-1 items-center justify-center p-2 sm:p-4'>
        {!live && (
          <button
            type='button'
            onClick={() => setPly(LIVE)}
            className='absolute top-3 left-1/2 z-10 -translate-x-1/2 rounded-full border
              border-alert bg-alert-soft px-3 py-1 text-xs font-medium text-alert shadow-sm'
          >
            Viewing move {ply} of {match.moves.length} — back to live
          </button>
        )}

        {
          /*
          No aspect ratio is imposed here. The SVG's own `preserveAspectRatio` fits the
          board inside whatever box it is given and centres it, so the board comes out
          right at any window shape without a chain of `min()` calculations that all have
          to be correct on every one of them.
        */
        }
        <div className='h-full w-full'>
          <Board
            board={game.game.position.board}
            cells={game.game.position.cells}
            selection={selection}
            targets={targets}
            viewFrom={viewFrom}
            toMove={game.toMove}
            interactive={myTurn}
            lastMove={game.lastMove}
            threatened={game.threatened}
            onSelect={setSelection}
            onPlay={play}
          />
        </div>
      </div>

      <aside className='flex w-full shrink-0 flex-col gap-3 border-t border-rule bg-ground p-3
          lg:w-80 lg:border-t-0 lg:border-l'>
        <SeatRow
          match={shown}
          seat={topSeat}
          game={game}
          live={live}
          clockOffset={clockOffset}
          selection={selection}
          onSelectReserve={null}
        />

        {match.status !== 'playing' && (
          <div className='rounded-md border border-rule bg-surface px-3 py-2 text-sm'>
            <strong className='font-semibold'>
              {OUTCOME[match.status] ?? match.status}
            </strong>
            {match.winner !== null && (
              <div className='mt-0.5 text-ink-2'>
                <UserName
                  userId={match.players[match.winner]}
                  seat={match.winner}
                />{' '}
                wins
              </div>
            )}
          </div>
        )}

        {/* Tabs exist only on a phone; a wide screen shows both panels at once. */}
        <div className='flex items-center gap-1 lg:hidden'>
          {(['moves', 'chat'] as const).map((name) => (
            <Button
              key={name}
              className='flex-1 capitalize'
              tone={drawer === name ? 'primary' : 'quiet'}
              onClick={() => setDrawer((current) => (current === name ? 'none' : name))}
            >
              {name}
              {name === 'moves' && match.moves.length > 0 && ` · ${match.moves.length}`}
              {name === 'chat' && match.chat.length > 0 && ` · ${match.chat.length}`}
            </Button>
          ))}
          <ConnectionBadge status={status} stale={stale} />
        </div>

        <Panel
          title='Moves'
          className={`max-h-56 min-h-32 lg:flex lg:max-h-none lg:min-h-40 lg:flex-1 ${
            drawer === 'moves' ? 'flex' : 'hidden'
          }`}
          actions={
            <span className='hidden lg:block'>
              <ConnectionBadge status={status} stale={stale} />
            </span>
          }
        >
          <MoveList moves={match.moves} ply={live ? -1 : ply} onSeek={setPly} />
        </Panel>

        <SeatRow
          match={shown}
          seat={bottomSeat}
          game={game}
          live={live}
          clockOffset={clockOffset}
          selection={selection}
          onSelectReserve={myTurn && mySeat === bottomSeat
            ? () =>
              setSelection((current) =>
                current?.kind === 'reserve' ? null : { kind: 'reserve' }
              )
            : null}
        />

        {mySeat !== null && playing && (
          <div className='flex gap-2'>
            <Button
              className='flex-1'
              disabled={!myTurn}
              onClick={() => play({ type: 'pass' })}
              title='Two passes in a row end the game as a draw'
            >
              Pass
            </Button>
            <Button
              tone='danger'
              className='flex-1'
              onClick={() => {
                if (confirm('Resign this game?')) play({ type: 'resign' });
              }}
            >
              Resign
            </Button>
          </div>
        )}

        <Chat
          match={match}
          canChat={mySeat !== null}
          hiddenOnMobile={drawer !== 'chat'}
          onSend={(text) => send({ t: 'chat', matchId: id, text })}
        />
      </aside>
    </div>
  );
}

function SeatRow({
  match,
  seat,
  game,
  live,
  clockOffset,
  selection,
  onSelectReserve,
}: {
  match: MatchState;
  seat: PlayerIndex;
  game: NonNullable<ReturnType<typeof useMatchGame>>;
  live: boolean;
  clockOffset: number;
  selection: Selection;
  onSelectReserve: (() => void) | null;
}) {
  const running = live && match.status === 'playing' && game.toMove === seat;

  return (
    <div
      className={`rounded-md border bg-surface px-3 py-2 transition-colors
        ${running ? 'border-accent' : 'border-rule'}`}
    >
      <div className='flex items-center justify-between gap-2'>
        <UserName userId={match.players[seat]} seat={seat} />
        <Clock
          match={match}
          player={seat}
          clockOffset={clockOffset}
          running={running}
        />
      </div>
      <div className='mt-1.5'>
        <Reserve
          count={game.game.position.spawns[seat]}
          player={seat}
          selected={selection?.kind === 'reserve' && Boolean(onSelectReserve)}
          onPick={onSelectReserve ?? undefined}
        />
      </div>
    </div>
  );
}

function Chat({
  match,
  canChat,
  hiddenOnMobile,
  onSend,
}: {
  match: MatchState;
  canChat: boolean;
  hiddenOnMobile: boolean;
  onSend(text: string): void;
}) {
  const [draft, setDraft] = useState('');

  return (
    <Panel
      title='Chat'
      className={`max-h-56 lg:flex ${hiddenOnMobile ? 'hidden' : 'flex'}`}
    >
      <div className='min-h-0 flex-1 overflow-y-auto px-3 py-2 text-sm'>
        {match.chat.length === 0
          ? <Empty>{canChat ? 'Say hello.' : 'Nothing said yet.'}</Empty>
          : match.chat.map((line) => (
            <p key={line.id} className='mb-1 last:mb-0'>
              <span className='mr-1.5 text-xs text-muted'>
                <UserName userId={line.userId} />
              </span>
              <span className='text-ink-2'>{line.text}</span>
            </p>
          ))}
      </div>
      {canChat && (
        <form
          className='flex gap-1 border-t border-rule p-2'
          onSubmit={(e) => {
            e.preventDefault();
            const text = draft.trim();
            if (!text) return;
            onSend(text);
            setDraft('');
          }}
        >
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={400}
            placeholder='Message'
            aria-label='Message'
            className='min-w-0 flex-1 rounded-md border border-rule bg-surface px-2 py-1 text-sm'
          />
          <Button type='submit' tone='primary'>Send</Button>
        </form>
      )}
    </Panel>
  );
}
